// tslint:disable:max-classes-per-file

import { Icon } from "armstrong-react";
import classNames from "classnames";
import React from "react";
import { Control } from "react-redux-form";
import _ from "underscore";
import * as zenscroll from "zenscroll";
import { isClickInside } from "../../../../../packages/ui/src/utils/uiHelpers";
import { IconButton, Loader } from "../atoms";

import "./searchComponent.scss";

interface ComponentProps<TItem> {
  className?: string;

  style?: React.CSSProperties;

  disabled?: boolean;

  /**
   * Object type of model
   */
  value?: TItem;

  getItemName: (item: TItem) => string;

  getItemId: (item: TItem) => number;

  getResults: (query: string) => Promise<TItem[]>;

  /**
   * If defined, this function will be called if getResults returns no items.
   * These will be displayed along with the error message.
   */
  getBackupResults?: () => Promise<TItem[]>;

  /**
   * Customise the error message when there are no results returned by getResults.
   */
  noResultsMessage?: (query?: string) => string;

  /**
   * Called when an item is selected from the list.
   */
  onChange?: (item: TItem) => void;

  /**
   * Called when the search input gains focus.
   */
  onFocus?: () => void;

  /**
   * Called when the search input loses focus, or is cleared.
   */
  onBlur?: () => void;

  /**
   * Called when the search input is cleared.
   */
  onClear?: () => void;

  inputProps?: React.HTMLProps<HTMLInputElement>;

  resultsProps?: React.HTMLProps<HTMLUListElement>;

  /**
   * Render an individual result. The second argument should be called if this item is selected.
   */
  renderResultItem?: (item: TItem, onSelect: () => void) => JSX.Element;

  /**
   * Render an error message. This is displayed in the results list.
   */
  renderErrorItem?: (message: string) => JSX.Element;

  /**
   * Render a container for the results. Useful for e.g. custom scrollbar behaviour.
   */
  renderResultsContainer?: (results: JSX.Element) => JSX.Element;

  /**
   * Customise the spinner on the right hand side of the search input
   */
  renderProgressSpinner?: (isSearching: boolean) => JSX.Element;

  /**
   * Override on click outside behaviour, useful when using custom results containers.
   */
  isClickInside?: (e: MouseEvent) => boolean;

  setRootRef?: (element: HTMLDivElement) => void;

  /**
   * Ids of items that should not be displayed in the results list.
   */
  excludeIds?: number[];
}

// Props only relevant to the containing component
interface OuterProps<TItem> {
  /**
   * react-redux-form model string - optional
   * If not set, rrf doesn't do anything (means we can use this component in non-rrf contexts)
   */
  model?: string;

  /**
   * If using this component in non-rrf context, set this value.
   */
  value?: TItem;
}

interface InnerState<TItem> {
  hasFocus: boolean;
  searchVal: string;
  searching: boolean;
  options: { text: string, item?: TItem }[];
  arrowSelectedIndex: number;
}

/**
 * Implementation of search/ select. Usable with react-redux-form <Control/>
 */
class SearchControl<TItem> extends React.Component<ComponentProps<TItem>, Partial<InnerState<TItem>>> {
  private inputRef: HTMLInputElement;
  private resultsRef: HTMLUListElement;
  private resultsScroller: ZenScroll.ZenScrollBase;

  public static defaultProps = {
    resultsProps: {},
  };

  public constructor(props: ComponentProps<TItem>) {
    super(props);
    this.state = {
      searchVal: props.value ? props.getItemName(props.value) : "",
      options: [],
    };
  }

  private setResultsRef = (ref: HTMLUListElement) => {
    this.resultsRef = ref;
    this.resultsScroller = zenscroll.createScroller(ref, 0, 0);
  }

  /**
   * Called whenever input gains focus
   */
  private onInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    const { value, getItemName, inputProps, onFocus } = this.props;
    const { searchVal } = this.state;

    // Show "default" results on first interaction
    if (!value && !searchVal || (value && searchVal === getItemName(value))) {
      this.search("");
    }

    (e.target as HTMLInputElement).select();

    this.openResults();

    if (inputProps && inputProps.onFocus) {
      inputProps.onFocus(e);
    }

    if (onFocus) {
      onFocus();
    }
  }

  /**
   * Called whenever input loses focus, or if search input is cleared
   */
  private onInputBlur = () => {
    this.inputRef.blur();
    this.setState({ hasFocus: false });

    const { onBlur } = this.props;
    if (onBlur) {
      onBlur();
    }
  }

  private onInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.value === "") {
      const { onChange } = this.props;
      if (onChange) {
        onChange(null);
      }
    }
    this.setState({ searchVal: event.target.value }, this.debouncedSearch);
  }

  /**
   * Keyboard navigation for search results
   */
  private onInputKeydown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const { arrowSelectedIndex, options } = this.state;

    switch (event.key) {
      case "Tab":
      case "Escape":
        this.onInputBlur();
        return;
      case "Enter":
        // Don't submit any parent form if we've got focus here
        event.preventDefault();

        if (arrowSelectedIndex != null && options.length > arrowSelectedIndex) {
          this.onResultSelect(options[arrowSelectedIndex].item);
          this.setState({ arrowSelectedIndex: null });
        }
        else {
          this.setState({ arrowSelectedIndex: 0 }, () => this.search(""));
        }
        return;
      case "ArrowDown":
      case "ArrowUp":
      case "PageDown":
      case "PageUp":
        event.preventDefault();
        break;
      default:
        return;
    }

    // Navigation key presses
    let nextIndex = arrowSelectedIndex > 0 ? arrowSelectedIndex : 0;
    switch (event.key) {
      case "ArrowDown":
        nextIndex++;
        break;
      case "PageDown":
        nextIndex += 5;
        break;
      case "ArrowUp":
        nextIndex--;
        break;
      case "PageUp":
        nextIndex -= 5;
        break;
    }

    nextIndex = Math.max(0, Math.min(nextIndex, options.length - 1));

    this.setState({ arrowSelectedIndex: nextIndex }, this.openResults);
  }

  private ensureArrowSelectionInView = () => {
    if (!this.resultsRef) {
      return;
    }

    const { arrowSelectedIndex } = this.state;

    const options = this.resultsRef.getElementsByTagName("li");
    if (arrowSelectedIndex < options.length) {
      const li = options[arrowSelectedIndex];
      if (li) {
        this.resultsScroller.intoView(li, 0);
      }
    }
  }

  /**
   * Populate state with results from the /locations API.
   */
  private search = (query?: string) => {
    if (this.state.searching) {
      return;
    }

    this.setState({ searching: true }, async () => {
      if (query == null) {
        query = this.state.searchVal;
      }

      const newState = {} as Partial<InnerState<TItem>>;
      try {
        const { getResults, getBackupResults, noResultsMessage, getItemId, getItemName, excludeIds } = this.props;

        const items = await getResults(query);
        if (items.length > 0) {
          const results = excludeIds
            ? items.filter(item => !excludeIds.some(excludedId => getItemId(item) === excludedId))
            : items;

          newState.options = results.map(item => ({ text: item ? getItemName(item) : "", item }));
        }
        else {
          // No results found...
          const messageItem = {
            text: noResultsMessage ? noResultsMessage(query) : `No results found matching "${query}"`,
          };

          // Get some items to show if the first query failed
          let backupItems = [];
          if (getBackupResults) {
            const results = await getBackupResults();
            backupItems = results.map(item => ({ text: item ? getItemName(item) : "", item }));
          }

          newState.options = [messageItem, ...backupItems];
        }
      }
      catch (e) {
        const err = e as IFetchApiFailure;
        newState.options = [
          {
            text: navigator.onLine
              ? `Sorry, something went wrong 😢. ${err.message}. Please try again later.`
              : "Couldn't get results - please check your connection then try again",
          },
        ];
      }

      newState.searching = false;
      this.setState(newState, this.openResults);
    });
  }

  private debouncedSearch = _.debounce(this.search, 400, false);

  private onResultSelect = (item: TItem) => {
    if (!item) {
      return;
    }

    const { onChange, getItemName } = this.props;

    if (onChange) {
      onChange(item);
    }

    this.setState({ searchVal: getItemName(item), hasFocus: false });
  }

  /**
   * Close results when clicked outside
   */
  private handleWindowClick = (e: MouseEvent) => {
    if (!this.inputRef || !this.resultsRef) {
      return;
    }

    let clickedInside: boolean;
    if (this.props.isClickInside) {
      clickedInside = this.props.isClickInside(e);
    }
    else {
      clickedInside = isClickInside(e, this.inputRef.getBoundingClientRect())
        || isClickInside(e, this.resultsRef.getBoundingClientRect());
    }

    if (!clickedInside) {
      this.onInputBlur();
      window.removeEventListener("click", this.handleWindowClick);
    }
  }

  /**
   * Open results. NB Can be called multiple times, won't keep adding new event handlers.
   */
  private openResults = () => {
    this.setState({ hasFocus: true }, () => {
      // #550 add window click handler *after* rerender to avoid button click triggering window click in IE11
      requestAnimationFrame(() => window.addEventListener("click", this.handleWindowClick));
    });
  }

  public componentWillReceiveProps(newProps: ComponentProps<TItem>) {
    if (!this.state.hasFocus) {
      // Reset on external state changes if component isn't being used
      this.setState({
        searchVal: newProps.value ? newProps.getItemName(newProps.value) : "",
        options: [],
      });
    }
  }

  public componentDidUpdate() {
    this.ensureArrowSelectionInView();
  }

  public componentWillUnmount() {
    window.removeEventListener("click", this.handleWindowClick);
  }

  /**
   * Render the icon on the right of the search input.
   */
  private renderSearchInputIcon = () => {
    if (this.state.searching) {
      return <Loader />;
    }
    else if (this.state.searchVal || this.state.hasFocus) {
      return (
        <IconButton
          className="search-input-icon"
          icomoon={Icon.Icomoon.cross2}
          onClick={() => {
            this.setState({ searchVal: "", hasFocus: false, options: [] });
            this.onInputBlur();

            const { onClear, onChange } = this.props;
            if (onClear) {
              onClear();
            }

            if (onChange) {
              onChange(null);
            }
          }}
        />
      );
    }
    else {
      return (
        <IconButton
          className="search-input-icon"
          icomoon={Icon.Icomoon.search2}
          onClick={() => this.inputRef && this.inputRef.select()}
        />
      );
    }
  }

  private renderResults = () => {
    const { getItemId, resultsProps, renderResultItem, renderErrorItem } = this.props;
    const { arrowSelectedIndex, hasFocus, options } = this.state;
    const { className, ...ulProps } = resultsProps;

    if (!options || !options.length) {
      // Don't render here - allows custom wrapper to detect when there are no results
      return;
    }

    return (
      <ul
        ref={this.setResultsRef}
        tabIndex={-1}
        className={classNames(className || "results")}
        {...ulProps}
      >
        {options.map((option, index) => (
          <li
            key={option.item ? getItemId(option.item) : index}
            className={classNames("result", { selected: arrowSelectedIndex === index })}
          >
            {!option.item && (
              renderErrorItem
                ? renderErrorItem(option.text)
                : (<span className="message">{option.text}</span>)
            )}

            {option.item && (renderResultItem
              ? renderResultItem(option.item, () => this.onResultSelect(option.item))
              : (
                <a onClick={() => this.onResultSelect(option.item)}>
                  {option.text}
                </a>
              )
            )}
          </li>
        ))}
      </ul>
    );
  }

  public render() {
    const { disabled, className, style, inputProps, renderResultsContainer, renderProgressSpinner, setRootRef } = this.props;
    const { searchVal, searching, hasFocus } = this.state;
    const hasInput = !!searchVal;

    return (
      <div
        ref={setRootRef}
        className={classNames("rrf-search-select", className, { "has-input": hasInput })}
        style={style}
      >
        <div className="rrf-search-select-input">
          <input
            ref={r => this.inputRef = r}
            type="search"
            value={searchVal}
            disabled={disabled}
            onClick={this.openResults}
            onChange={this.onInputChange}
            onKeyDownCapture={this.onInputKeydown}
            {...inputProps}
            onFocus={this.onInputFocus}
            className={classNames(inputProps ? inputProps.className : "", { "has-input": hasInput })}
          />

          {(renderProgressSpinner
            ? renderProgressSpinner(searching)
            : this.renderSearchInputIcon()
          )}
        </div>

        {hasFocus && (renderResultsContainer
          ? renderResultsContainer(this.renderResults())
          : this.renderResults()
        )}
      </div>
    );
  }
}

export type SearchComponentProps<TItem> = ComponentProps<TItem> & OuterProps<TItem>;

export class SearchComponent<TItem> extends React.Component<SearchComponentProps<TItem>> {
  public render() {
    const props = this.props as any;

    if (props.model) {
      return (
        <Control component={SearchControl} model="" {...props} />
      );
    }
    else {
      return <SearchControl {...props} />;
    }
  }
}
